<template>
  <card card-body-classes="table-full-width">
    <div slot="header" class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
      <h4 slot="header" class="card-title">{{ $t('accountJourneys.title') }}</h4>
    </div>
    <div >
      <div
        class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
      >
        <el-select
          class="select-primary mb-3 pagination-select"
          v-model="pagination.perPage"
          placeholder="Per page"
        >
          <el-option
            class="select-primary"
            v-for="item in pagination.perPageOptions"
            :key="item"
            :label="item"
            :value="item"
          >
          </el-option>
        </el-select>

        <div class="row">
          <div class="col-6">
            <base-input>
              <el-input
                type="search"
                class="mb-3 search-input"
                prefix-icon="el-icon-search"
                :placeholder="$t('generic.searchInTablePlaceholder')"
                v-model="searchQuery"
                aria-controls="datatables"
              >
              </el-input>
            </base-input>
          </div>

        </div>
      </div>

      <el-table v-loading="loading" :data="queriedData" :empty-text="$t('generic.noData')" :row-class-name="journeyTableRowClassName">
        <el-table-column type="expand">
          <template slot-scope="props">
            <journey-details :details="props.row"/>
          </template>
        </el-table-column>
        <el-table-column
          v-for="column in tableColumns"
          :key="column.label"
          :min-width="column.minWidth"
          :prop="column.prop"
          :label="column.label"
          :formatter="column.formatter"
        >
        </el-table-column>
        <el-table-column align="right" width="50">
          <div slot-scope="props">
            <router-link
              :to="{name: 'account-journey-details', params: {id: props.row.account_id, journey_id: props.row.id.String}}">
              <i class="tim-icons icon-double-right"></i>
            </router-link>
          </div>
        </el-table-column>
      </el-table>
    </div>
    <div
      slot="footer"
      class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
    >
      <div class="">
        <p class="card-category">
          {{ $t('generic.tablePaginationSummary', {from: from + 1, to: to, total: total}) }}
        </p>
      </div>
      <base-pagination
        class="pagination-no-border"
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
      >
      </base-pagination>
    </div>
  </card>
</template>

<script>
import {Option, Select, Table, TableColumn} from 'element-ui';
import {BaseAlert, BasePagination} from 'src/components';
import Fuse from 'fuse.js';
import {mapActions, mapState} from "vuex";
import JourneyDetails from "@/components/Journey/JourneyDetails";

export default {
  name: 'user-journeys',
  components: {
    BasePagination,
    BaseAlert,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    JourneyDetails
  },
  props: {
    accountId: {
      type: String,
      required: true
    },
  },
  computed: {
    ...mapState({
      loading: state => state.account_journeys.loading,
      data: state => state.account_journeys.data,
    }),
    /***
     * Returns a page from the searched data or the whole data. Search is performed in the watch section below
     */
    queriedData() {
      let result = this.tableData;
      if (this.searchedData.length > 0) {
        result = this.searchedData;
      }
      return result.slice(this.from, this.to);
    },
    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    total() {
      return this.searchedData.length > 0
        ? this.searchedData.length
        : this.tableData.length;
    },
    // Table columns need to be computed to dynamically change the headers when language is selected
    tableColumns() {
      return [
        {
          prop: 'operation_ts',
          label: this.$t('journey.details.operation_ts'),
          formatter: (row, column, cellValue, index) => {
            return this.$options.filters.time(cellValue)
          },
          minWidth: 200
        },
        {
          prop: 'discount_id',
          label: this.$t('journey.details.discount_id'),
          minWidth: 150
        },
        {
          prop: 'fare_municipality_id',
          label: this.$t('journey.details.fare_municipality_id'),
          minWidth: 150
        },
        {
          prop: 'ticket_type',
          label: this.$t('journey.details.ticket_type'),
          minWidth: 120
        },
        {
          prop: 'charge',
          label: this.$t('journey.details.charge'),
          formatter: (row, column, cellValue, index) => {
            return this.$options.filters.price(cellValue)
          },
          minWidth: 100
        },
        {
          prop: 'route',
          label: this.$t('journey.details.route'),
          minWidth: 120
        },
        {
          prop: 'operator_id',
          label: this.$t('journey.details.operator_id'),
        },
      ]
    }
  },
  data() {
    return {
      pagination: {
        perPage: 50,
        currentPage: 1,
        perPageOptions: [5, 10, 25, 50],
        total: 0
      },
      searchQuery: '',
      propsToSearch: ['operation_ts', 'discount_id', 'ticket_type', 'garage_number', 'operator_id', 'route', 'vehicle_id', "vehicle_type"],
      tableData: [],
      searchedData: [],
      fuseSearch: null
    };
  },
  methods: {
    ...mapActions(['LoadAccountJourneys']),
    journeyTableRowClassName(data) {
      if (data.row.async) {
        return 'special_row';
      }
      return ''
    }
  },
  mounted() {
    this.LoadAccountJourneys(this.accountId)
  },
  watch: {
    /**
     * Searches through the table data by a given query.
     * NOTE: If you have a lot of data, it's recommended to do the search on the Server Side and only display the results here.
     * @param value of the query
     */
    searchQuery(value) {
      let result = this.tableData;
      if (value !== '') {
        result = this.fuseSearch.search(this.searchQuery);
      }
      this.searchedData = result;
    },
    data(journeys) {
      this.tableData = journeys;
      // Fuse search initialization.
      this.fuseSearch = new Fuse(this.tableData, {
        keys: this.propsToSearch,
        threshold: 0.3
      });
    }
  }
};
</script>
